import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { type ProjectMember } from '@/services/projectMemberService'
import { dateLocaleFor } from '@/lib/dateLocale'

interface ProjectMemberDetailsModalProps {
  onSuccess?: () => void
}

export interface ProjectMemberDetailsModalRef {
  open: (member: ProjectMember) => void
  close: () => void
}

const ProjectMemberDetailsModal = forwardRef<ProjectMemberDetailsModalRef, ProjectMemberDetailsModalProps>(
  ({ onSuccess }, ref) => {
    const { t, i18n } = useTranslation('projectMembers')
    const [isOpen, setIsOpen] = useState(false)
    const [member, setMember] = useState<ProjectMember | null>(null)

    useImperativeHandle(ref, () => ({
      open: (memberData: ProjectMember) => {
        setMember(memberData)
        setIsOpen(true)
      },
      close: () => {
        setIsOpen(false)
        setMember(null)
      },
    }))

    if (!isOpen || !member) return null

    const getRoleColor = (role: string) => {
      switch (role) {
        case 'owner':
          return 'bg-purple-100 text-purple-800 border-purple-200'
        case 'admin':
          return 'bg-indigo-100 text-indigo-800 border-indigo-200'
        case 'member':
          return 'bg-blue-100 text-blue-800 border-blue-200'
        case 'viewer':
          return 'bg-green-100 text-green-800 border-green-200'
        case 'observer':
          return 'bg-gray-100 text-gray-800 border-gray-200'
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200'
      }
    }

    const getPlatformRoleColor = (role: string) => {
      switch (role) {
        case 'owner':
          return 'bg-purple-100 text-purple-800 border-purple-200'
        case 'admin':
          return 'bg-indigo-100 text-indigo-800 border-indigo-200'
        case 'auditor':
          return 'bg-amber-100 text-amber-800 border-amber-200'
        case 'security':
          return 'bg-red-100 text-red-800 border-red-200'
        case 'user':
          return 'bg-blue-100 text-blue-800 border-blue-200'
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200'
      }
    }

    const getTypeColor = (type: string) => {
      switch (type) {
        case 'local':
          return 'bg-blue-100 text-blue-800 border-blue-200'
        case 'ldap':
          return 'bg-orange-100 text-orange-800 border-orange-200'
        case 'oidc':
          return 'bg-green-100 text-green-800 border-green-200'
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200'
      }
    }

    const formatDate = (dateString: string) => {
      if (!dateString) return '-'
      try {
        const date = new Date(dateString)
        const locale = dateLocaleFor(i18n.language)

        const formattedDate = date.toLocaleString(locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        })

        return formattedDate
      } catch {
        return dateString
      }
    }

    const handleClose = () => {
      setIsOpen(false)
      setMember(null)
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-primary/5 to-transparent">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-sm shrink-0">
                  <span className="text-white text-lg font-semibold">
                    {member.user.name ? member.user.name.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{t('detailsModal.title')}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {member.user.name} · @{member.user.account}
                  </p>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Basic Information Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {t('detailsModal.sections.basic')}
                </h3>
                <div className="space-y-4">
                  {/* User ID */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      {t('detailsModal.fields.userId')}
                    </label>
                    <div className="text-sm text-gray-900 bg-gray-50 px-3.5 py-2.5 rounded-lg border border-gray-200 font-mono">
                      {member.user.id}
                    </div>
                  </div>

                  {/* Account */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      {t('detailsModal.fields.account')}
                    </label>
                    <div className="text-sm text-gray-900 bg-gray-50 px-3.5 py-2.5 rounded-lg border border-gray-200">
                      {member.user.account}
                    </div>
                  </div>

                  {/* Display Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      {t('detailsModal.fields.displayName')}
                    </label>
                    <div className="text-sm text-gray-900 bg-gray-50 px-3.5 py-2.5 rounded-lg border border-gray-200">
                      {member.user.name}
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Membership Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {t('detailsModal.sections.membership')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Project Role */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      {t('detailsModal.fields.projectRole')}
                    </label>
                    <div className="min-h-[44px] h-[44px] px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg flex items-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getRoleColor(
                          member.role
                        )}`}
                      >
                        {t(`projectRoles.${member.role}`, member.role)}
                      </span>
                    </div>
                  </div>

                  {/* Joined At */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      {t('detailsModal.fields.joinedAt')}
                    </label>
                    <div className="text-sm text-gray-900 bg-gray-50 px-3.5 py-2.5 rounded-lg border border-gray-200 flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {formatDate(member.created_at)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Settings Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {t('detailsModal.sections.account')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Platform Role */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      {t('detailsModal.fields.platformRole')}
                    </label>
                    <div className="min-h-[44px] h-[44px] px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg flex items-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getPlatformRoleColor(
                          member.user.platform_role
                        )}`}
                      >
                        {t(`platformRoles.${member.user.platform_role}`, member.user.platform_role)}
                      </span>
                    </div>
                  </div>

                  {/* Account Type */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      {t('detailsModal.fields.accountType')}
                    </label>
                    <div className="min-h-[44px] h-[44px] px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg flex items-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getTypeColor(
                          member.user.type
                        )}`}
                      >
                        {t(`types.${member.user.type}`, member.user.type)}
                      </span>
                    </div>
                  </div>

                  {/* 2FA Status */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      {t('detailsModal.fields.twoFactor')}
                    </label>
                    <div className="min-h-[44px] h-[44px] px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-2">
                      {member.user.totp_enabled ? (
                        <>
                          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-green-700 font-medium">{t('detailsModal.state.enabled')}</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-gray-600">{t('detailsModal.state.disabled')}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Terms Agreement */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      {t('detailsModal.fields.termsAgreed')}
                    </label>
                    <div className="min-h-[44px] h-[44px] px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-2">
                      {member.user.contract_agreement ? (
                        <>
                          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-green-700 font-medium">{t('detailsModal.state.agreed')}</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-gray-600">{t('detailsModal.state.notAgreed')}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Information Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('detailsModal.sections.activity')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* User Created At */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      {t('detailsModal.fields.createdAt')}
                    </label>
                    <div className="text-sm text-gray-900 bg-gray-50 px-3.5 py-2.5 rounded-lg border border-gray-200 flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {formatDate(member.user.created_at)}
                    </div>
                  </div>

                  {/* User Updated At */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      {t('detailsModal.fields.updatedAt')}
                    </label>
                    <div className="text-sm text-gray-900 bg-gray-50 px-3.5 py-2.5 rounded-lg border border-gray-200 flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      {formatDate(member.user.updated_at)}
                    </div>
                  </div>

                  {/* Last Login */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      {t('detailsModal.fields.lastLoginAt')}
                    </label>
                    <div className="text-sm text-gray-900 bg-gray-50 px-3.5 py-2.5 rounded-lg border border-gray-200 flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      {formatDate(member.user.last_login_at || '')}
                    </div>
                  </div>
                </div>
              </div>

              {/* External user info */}
              {member.user.type !== 'local' && (
                <div className="bg-amber-50/50 border border-amber-200/50 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 text-amber-600 shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-amber-900 mb-1">
                        {t('detailsModal.external.title')}
                      </p>
                      <p className="text-xs text-amber-700 leading-relaxed">
                        {t('detailsModal.external.description')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 px-6 py-4 bg-gray-50/50 flex items-center justify-end gap-3 shrink-0">
            <button
              onClick={handleClose}
              className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all shadow-sm"
            >
              {t('detailsModal.close')}
            </button>
          </div>
        </div>
      </div>
    )
  }
)

ProjectMemberDetailsModal.displayName = 'ProjectMemberDetailsModal'

export default ProjectMemberDetailsModal
